import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList, ArrowLeft, FileText, Link as LinkIcon, MessageSquare, Image as ImageIcon, CheckCircle2, AlertCircle, Clock, Calendar, User, ExternalLink, History, TrendingUp, ShieldX, Pencil, Repeat, Target, Trophy, ChevronRight, Sparkles } from 'lucide-react';
import { addSubmission, getSubmissions, updateSubmission } from '../actions/submissionActions';
import { getStudentSubmissionForms, getStudentSubmissionForm } from '../actions/submissionFormActions';
import ImageUpload from '../components/common/ImageUpload';
import VideoRecorder from '../components/common/VideoRecorder';
import RichTextRenderer from '../components/common/RichTextRenderer';
import CollaboratorSelect from '../components/common/CollaboratorSelect';

const StudentSubmissionForms = ({ auth, submission, submissionForm, addSubmission, getSubmissions, getStudentSubmissionForms, getStudentSubmissionForm, updateSubmission }) => {
  const navigate = useNavigate();
  const { formId: urlFormId } = useParams();
  const [selectedForm, setSelectedForm] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submissionData, setSubmissionData] = useState({
    link: '',
    comment: '',
    achievementImages: [],
    eventDate: '',
    eventTitle: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSubmissionHistory, setShowSubmissionHistory] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionJustAdded, setSubmissionJustAdded] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [editingSubmissionId, setEditingSubmissionId] = useState(null);
  const [editingAdminComment, setEditingAdminComment] = useState('');
  const [activeTab, setActiveTab] = useState('repeatable');

  const { user } = auth;
  // Make sure we get submissions as an array
  const { submissions = [], loading: submissionsLoading } = submission || {};

  // Get program submission forms from dedicated API (not from bootstrap user data)
  const programSubmissionForms = submissionForm?.submissionForms || [];

  // Load user's submissions and program forms when component mounts
  useEffect(() => {
    if (user?.id) {
      getSubmissions({ userId: user.id });
      getStudentSubmissionForms();
    }
  }, [user?.id, getSubmissions, getStudentSubmissionForms]);

  // Debug submissions data
  useEffect(() => {
    if (submissions && submissions.length > 0) {
      // Submissions loaded successfully
    }
  }, [submissions]);


  // Check if user is student and has submission forms
  useEffect(() => {
    if (!user || user.role !== 'student') {
      navigate(urlFormId && user ? `/content-management/submission-forms/${urlFormId}/view` : '/dashboard');
    }
  }, [user, navigate, urlFormId]);

  // Auto-select form from URL parameter
  useEffect(() => {
    if (!urlFormId || selectedForm || user?.role !== 'student') return;
    // Server checks the form is in the student's program or one of its courses
    getStudentSubmissionForm(urlFormId)
      .then(form => {
        setSelectedForm(form);
        setAccessDenied(false);
      })
      .catch(() => setAccessDenied(true));
  }, [urlFormId]);

  // Handle form selection
  const handleFormSelect = (form) => {
    setSelectedForm(form);
    // Update URL with form ID for repeatable forms
    if (form.repeatable) {
      navigate(`/submissions/${form._id}`, { replace: true });
    }
    // Clear form data for new submissions
    setSubmissionData({
      link: '',
      comment: '',
      achievementImages: [],
      eventDate: '',
      eventTitle: ''
    });
    setCollaborators([]);
    setEditingSubmissionId(null);
    setEditingAdminComment('');
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedForm(null);
    setAccessDenied(false);
    navigate('/submissions', { replace: true });
    setSubmissionData({
      link: '',
      comment: '',
      achievementImages: [],
      eventDate: '',
      eventTitle: ''
    });
    setCollaborators([]);
    setEditingSubmissionId(null);
    setEditingAdminComment('');
    // Don't show success modal when going back
    setShowSuccessModal(false);
    setShowSubmissionHistory(false);
  };

  // Validate URL
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Check if a one-time form has already been submitted
  // Allow resubmission if latest status is rejected or more_info_needed
  const hasOneTimeSubmission = (form) => {
    if (!form || form.repeatable) return false;
    const formSubmissions = getFormSubmissions(form._id);
    if (formSubmissions.length === 0) return false;
    const latestStatus = formSubmissions[0].status;
    if (latestStatus === 'rejected' || latestStatus === 'more_info_needed') return false;
    return true;
  };

  // Check if form can be submitted
  const canSubmitForm = (form) => {
    if (!form) return false;

    // One-time forms can only be submitted once (unless editing a rejected/more_info_needed submission)
    if (!editingSubmissionId && hasOneTimeSubmission(form)) return false;

    // Check required fields
    if (form.linkRequired && !submissionData.link.trim()) return false;
    if (submissionData.link && submissionData.link.trim() && !validateUrl(submissionData.link)) return false;
    if (form.commentRequired && !submissionData.comment.trim()) return false;
    if (form.achievementImageRequired && (!submissionData.achievementImages || submissionData.achievementImages.length === 0)) return false;
    if (form.videoRequired && !submissionData.video) return false;

    // Repeatable forms require eventDate and eventTitle
    if (form.repeatable) {
      if (!submissionData.eventDate) return false;
      if (new Date(submissionData.eventDate) > new Date()) return false;
      if (!submissionData.eventTitle.trim()) return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmitForm = async () => {
    if (!selectedForm || !canSubmitForm(selectedForm)) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionJustAdded(false);

    const submissionPayload = {
      submissionFormId: selectedForm._id,
      userId: user.id
    };

    if (submissionData.link) {
      submissionPayload.link = submissionData.link;
    }
    if (submissionData.comment) {
      submissionPayload.comment = submissionData.comment;
    }
    if (submissionData.achievementImages) {
      submissionPayload.achievementImages = submissionData.achievementImages;
    }
    if (selectedForm.videoRequired) {
      submissionPayload.video = submissionData.video || '';
    }
    // Include optional repeatable-form fields
    if (selectedForm.repeatable) {
      if (submissionData.eventDate) {
        submissionPayload.eventDate = submissionData.eventDate;
      }
      if (submissionData.eventTitle) {
        submissionPayload.eventTitle = submissionData.eventTitle;
      }
    }
    // Include collaborators
    if (collaborators.length > 0) {
      submissionPayload.collaborators = collaborators.map(u => u._id);
    }

    try {
      if (editingSubmissionId) {
        // Update existing submission (resubmission for rejected/more_info_needed)
        await updateSubmission(editingSubmissionId, submissionPayload);
        setEditingSubmissionId(null);
        setEditingAdminComment('');
      } else {
        await addSubmission(submissionPayload);
      }
      setShowSuccessModal(true);
      setSubmissionJustAdded(true);
      // Refresh submissions list
      getSubmissions({ userId: user.id });
      // Clear form after successful submission
      setSubmissionData({
        link: '',
        comment: '',
        achievementImages: [],
        eventDate: '',
        eventTitle: ''
      });
      setCollaborators([]);
    } catch (err) {
      // Error is already dispatched to Redux error state via the action
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get submissions for a specific form
  const getFormSubmissions = (formId) => {
    if (!submissions || !Array.isArray(submissions) || !formId) {
      return [];
    }
    
    const filtered = submissions.filter(sub => {
      // Handle both populated and non-populated submissionFormId
      const subFormId = sub.submissionFormId?._id || sub.submissionFormId;
      return subFormId === formId;
    }).sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
    
    return filtered;
  };

  // Get submission count for a form
  const getSubmissionCount = (formId) => {
    return getFormSubmissions(formId).length;
  };

  // Get latest submission status for a form
  const getLatestSubmissionStatus = (formId) => {
    const formSubmissions = getFormSubmissions(formId);
    return formSubmissions.length > 0 ? formSubmissions[0].status : null;
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'more_info_needed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-muted text-foreground/60';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="w-3 h-3" />;
      case 'rejected': return <AlertCircle className="w-3 h-3" />;
      case 'pending': return <Clock className="w-3 h-3" />;
      case 'more_info_needed': return <AlertCircle className="w-3 h-3" />;
      default: return <Clock className="w-3 h-3" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'more_info_needed': return 'More Info Needed';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Handle file upload
  const handleFileUpload = (images) => {
    setSubmissionData(prev => ({ ...prev, achievementImages: images }));
  };

  // Success Modal Component
  const SuccessModal = () => {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-card rounded-lg p-8 max-w-md w-full mx-4 text-center">
          <div className="mb-6">
            <div className="p-4 bg-green-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Submission Successful!</h3>
            <p className="text-foreground/60">Your work has been submitted and is waiting for review.</p>
          </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-blue-800 text-sm">
            <Clock className="w-4 h-4" />
            <span>Status: Pending Review</span>
          </div>
          <p className="text-blue-700 text-sm mt-1">
            You'll receive feedback once an instructor reviews your submission.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => {
              setShowSuccessModal(false);
              // Refresh submissions data when closing
              getSubmissions({ userId: user.id });
            }}
            className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:opacity-80 transition-colors"
          >
            Continue
          </button>
          <button
            onClick={() => {
              setShowSuccessModal(false);
              setShowSubmissionHistory(true);
              // Force refresh submissions data before showing history
              getSubmissions({ userId: user.id });
            }}
            className="flex-1 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors"
          >
            View History
          </button>
        </div>
      </div>
    </div>
  );
  };

  // Submission History Modal Component
  const SubmissionHistoryModal = () => {
    const formSubmissions = getFormSubmissions(selectedForm?._id);
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        style={{ zIndex: 9999 }}
      >
        <div className="bg-card rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <History className="w-6 h-6 text-[#1086a4]" />
              <h3 className="text-xl font-bold text-foreground">Submission History</h3>
            </div>
            <button
              onClick={() => {
                setShowSubmissionHistory(false);
                // If this was opened from the list view, clear the selected form
                if (selectedForm?._isHistoryOnly) {
                  setSelectedForm(null);
                }
              }}
              className="text-foreground/50 hover:text-foreground"
            >
              ✕
            </button>
          </div>

          <div className="mb-4 p-4 bg-muted rounded-lg">
            <h4 className="font-semibold text-foreground mb-1">{selectedForm?.title}</h4>
            <p className="text-sm text-foreground/60">Total Submissions: {formSubmissions.length}</p>
            {formSubmissions.length > 0 && (
              <p className="text-xs text-foreground/50 mt-1">
                Latest: {new Date(formSubmissions[0].submittedAt).toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="overflow-y-auto max-h-[60vh]">
            {submissionsLoading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-[#1086a4] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-gray-500">Loading submissions...</p>
              </div>
            ) : formSubmissions.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="w-12 h-12 text-foreground/30 mx-auto mb-3" />
                <p className="text-gray-500">No submissions yet</p>
                <p className="text-xs text-gray-400 mt-2">
                  Total submissions in state: {submissions.length}
                </p>
                <p className="text-xs text-gray-400">
                  Looking for form ID: {selectedForm?._id}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {formSubmissions.map((sub, index) => (
                  <div key={sub._id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground/50">#{formSubmissions.length - index}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(sub.status)}`}>
                          {getStatusIcon(sub.status)}
                          {getStatusLabel(sub.status)}
                        </span>
                      </div>
                      <div className="text-right text-sm text-foreground/50">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(sub.submittedAt).toLocaleDateString()}
                        </div>
                        <div>{new Date(sub.submittedAt).toLocaleTimeString()}</div>
                      </div>
                    </div>

                    {sub.eventTitle && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-foreground/70">Title/Topic:</span>
                        <span className="ml-2 text-sm text-foreground/60">{sub.eventTitle}</span>
                      </div>
                    )}

                    {sub.eventDate && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-foreground/70">Date of Event/Post:</span>
                        <span className="ml-2 text-sm text-foreground/60">{new Date(sub.eventDate).toLocaleDateString()}</span>
                      </div>
                    )}

                    {sub.link && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-foreground/70">Link:</span>
                        <a 
                          href={sub.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="ml-2 text-[#1086a4] hover:text-[#054581] text-sm flex items-center gap-1 inline-flex"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {sub.link.length > 50 ? sub.link.substring(0, 50) + '...' : sub.link}
                        </a>
                      </div>
                    )}

                    {sub.comment && (
                      <div className="mb-2">
                        <span className="text-sm font-medium text-foreground/70">Comment:</span>
                        <p className="text-sm text-foreground/60 mt-1">{sub.comment}</p>
                      </div>
                    )}

                    {sub.adminComment && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-800">Instructor Feedback:</span>
                        </div>
                        <p className="text-sm text-yellow-700">{sub.adminComment}</p>
                      </div>
                    )}

                    {sub.collaborators && sub.collaborators.length > 0 && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                        <User className="w-3.5 h-3.5 text-[#1086a4]" />
                        <span className="text-sm font-medium text-foreground/70">Collaborated with:</span>
                        <div className="flex flex-wrap gap-1.5">
                          {sub.collaborators.map(collab => (
                            <span
                              key={collab._id || collab}
                              className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                            >
                              {collab.name ? `${collab.name} ${collab.lastName || ''}`.trim() : 'Unknown'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Edit button for rejected / more_info_needed submissions */}
                    {['rejected', 'more_info_needed'].includes(sub.status) && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <button
                          onClick={() => {
                            setSubmissionData({
                              link: sub.link || '',
                              comment: sub.comment || '',
                              achievementImages: sub.achievementImages?.length ? sub.achievementImages : [],
                              eventDate: sub.eventDate ? new Date(sub.eventDate).toISOString().split('T')[0] : '',
                              eventTitle: sub.eventTitle || '',
                              video: sub.videoUrl || ''
                            });
                            setEditingSubmissionId(sub._id);
                            setEditingAdminComment(sub.adminComment || '');
                            setShowSubmissionHistory(false);
                            // If opened from list view, switch to form view
                            if (selectedForm?._isHistoryOnly) {
                              setSelectedForm({ ...selectedForm, _isHistoryOnly: false });
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit & Resubmit
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <button
              onClick={() => {
                setShowSubmissionHistory(false);
                // If this was opened from the list view, clear the selected form
                if (selectedForm?._isHistoryOnly) {
                  setSelectedForm(null);
                }
              }}
              className="px-6 py-2 bg-muted text-foreground rounded-lg hover:opacity-80 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show selected form details
  if (selectedForm && !selectedForm._isHistoryOnly) {
    return (
      <div className="min-h-screen pt-20 pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={handleBackToList}
              className="inline-flex items-center gap-2 text-[#1086a4] hover:text-[#054581] font-medium mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Submission Forms
            </button>
            
            <div className="bg-card rounded-lg shadow-lg border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">{selectedForm.title}</h1>
                  <p className="text-foreground/60">{selectedForm.repeatable ? 'Submit your work multiple times to track your progress' : 'Submit your work for review'}</p>
                </div>
                <div className="p-4 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
                  <ClipboardList className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              
              {/* Assignment Description */}
              {selectedForm.description && (
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Assignment Description:</h4>
                  <RichTextRenderer
                    content={selectedForm.description}
                    className="text-[#1e2d40]"
                    fontSize="14px"
                    allowInlineStyles={true}
                  />
                </div>
              )}

            </div>
          </div>

          {/* Submission Form */}
          <div className="bg-card rounded-lg shadow-lg border border-border p-6">
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[#1086a4]" />
                <h3 className="text-lg font-semibold text-foreground">Submit Your Work</h3>
              </div>

              {/* Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Requirements:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className={`flex items-center gap-2 p-2 rounded ${selectedForm.linkRequired ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                    <LinkIcon className="w-4 h-4" />
                    <span>Link {selectedForm.linkRequired ? '(Required)' : '(Optional)'}</span>
                  </div>
                  <div className={`flex items-center gap-2 p-2 rounded ${selectedForm.commentRequired ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    <MessageSquare className="w-4 h-4" />
                    <span>Comment {selectedForm.commentRequired ? '(Required)' : '(Optional)'}</span>
                  </div>
                  <div className={`flex items-center gap-2 p-2 rounded ${selectedForm.achievementImageRequired ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>
                    <ImageIcon className="w-4 h-4" />
                    <span>Image {selectedForm.achievementImageRequired ? '(Required)' : '(Optional)'}</span>
                  </div>
                </div>
              </div>

              {/* Editing notice for resubmission */}
              {editingSubmissionId && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-orange-800 flex items-center gap-2">
                      <Pencil className="w-4 h-4" />
                      Editing Previous Submission
                    </h4>
                    <button
                      onClick={() => {
                        setEditingSubmissionId(null);
                        setEditingAdminComment('');
                        setSubmissionData({ link: '', comment: '', achievementImages: [], eventDate: '', eventTitle: '' });
                      }}
                      className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                    >
                      Cancel Edit
                    </button>
                  </div>
                  <p className="text-sm text-orange-700 mb-1">
                    Make your changes below and click "Resubmit Work" when ready.
                  </p>
                  {editingAdminComment && (
                    <div className="mt-2 bg-white border border-orange-200 rounded p-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-orange-600">Instructor Feedback:</span>
                      <p className="text-sm text-orange-800 mt-1">{editingAdminComment}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Link Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Project Link {selectedForm.linkRequired && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="url"
                    value={submissionData.link}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="https://your-project-link.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent ${
                      submissionData.link && submissionData.link.trim() && !validateUrl(submissionData.link) ? 'border-red-500' : 'border-border'
                    }`}
                    required={selectedForm.linkRequired}
                  />
                  {submissionData.link && submissionData.link.trim() && !validateUrl(submissionData.link) && (
                    <p className="text-red-500 text-sm mt-1">Please enter a valid URL</p>
                  )}
                </div>

                {/* Comment Input */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description/Comment {selectedForm.commentRequired && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    value={submissionData.comment}
                    onChange={(e) => setSubmissionData(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Describe your work, challenges faced, what you learned..."
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                    required={selectedForm.commentRequired}
                  />
                </div>

                {/* Achievement Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Screenshot/Image {selectedForm.achievementImageRequired && <span className="text-red-500">*</span>}
                  </label>
                  <ImageUpload
                    value={submissionData.achievementImages}
                    onChange={handleFileUpload}
                    required={selectedForm.achievementImageRequired}
                    multiple={true}
                  />
                </div>

                {/* Video Recording */}
                {selectedForm.videoRequired && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Video Recording <span className="text-red-500">*</span>
                    </label>
                    <VideoRecorder
                      value={submissionData.video}
                      onChange={(video) => setSubmissionData(prev => ({ ...prev, video }))}
                      instructions={selectedForm.videoInstructions}
                    />
                  </div>
                )}

                {/* Add Collaborators */}
                {auth.token && selectedForm.collaboratorsEnabled !== false && (
                  <CollaboratorSelect
                    value={collaborators}
                    onChange={setCollaborators}
                    tokenConfig={{ headers: { Authorization: `Bearer ${auth.token}` } }}
                  />
                )}

                {/* Date of Event/Post and Title/Topic - only for repeatable forms */}
                {selectedForm.repeatable && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Date of Event/Post <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={submissionData.eventDate}
                        onChange={(e) => setSubmissionData(prev => ({ ...prev, eventDate: e.target.value }))}
                        max={new Date().toISOString().split('T')[0]}
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent ${
                          submissionData.eventDate && new Date(submissionData.eventDate) > new Date() ? 'border-red-500' : 'border-border'
                        }`}
                      />
                      {submissionData.eventDate && new Date(submissionData.eventDate) > new Date() && (
                        <p className="text-red-500 text-sm mt-1">Date must be in the past</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Title/Topic <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={submissionData.eventTitle}
                        onChange={(e) => setSubmissionData(prev => ({ ...prev, eventTitle: e.target.value }))}
                        placeholder="Enter the title or topic of your event/post"
                        required
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-[#1086a4] focus:border-transparent"
                      />
                    </div>
                  </>
                )}

                {/* Embedded Content (Google Form, Sheet, PDF, etc.) */}
                {(selectedForm.embedUrls?.length > 0 || selectedForm.embedUrl) && (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-foreground">Embedded Content</label>
                    {(selectedForm.embedUrls || (selectedForm.embedUrl ? [selectedForm.embedUrl] : [])).map((url, i) => (
                      <iframe
                        key={i}
                        src={url}
                        title={`Embedded content ${i + 1}`}
                        className="w-full border border-gray-200 rounded-lg"
                        style={{ height: '600px' }}
                        allowFullScreen
                        allow="autoplay; encrypted-media"
                      />
                    ))}
                  </div>
                )}

                {/* Submit button */}
                <div className="flex justify-between items-center pt-4 border-t border-border">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-foreground/50">
                      {selectedForm.repeatable
                        ? '💡 You can submit this assignment multiple times to track your progress'
                        : '💡 Submit your work for instructor review'}
                    </p>
                    {getSubmissionCount(selectedForm._id) > 0 && (
                      <button
                        onClick={() => {
                          setShowSubmissionHistory(true);
                        }}
                        className="inline-flex items-center gap-2 text-[#1086a4] hover:text-[#054581] text-sm font-medium transition-colors"
                      >
                        <History className="w-4 h-4" />
                        View History ({getSubmissionCount(selectedForm._id)})
                      </button>
                    )}
                  </div>
                  <button
                    onClick={handleSubmitForm}
                    disabled={!canSubmitForm(selectedForm) || isSubmitting}
                    className={`px-8 py-3 rounded-lg font-semibold transition-all ${
                      canSubmitForm(selectedForm) && !isSubmitting
                        ? 'bg-[#1086a4] hover:bg-[#054581] text-white shadow-lg hover:shadow-xl'
                        : 'bg-muted text-foreground/50 cursor-not-allowed'
                    }`}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {editingSubmissionId ? 'Resubmitting...' : 'Submitting...'}
                      </div>
                    ) : hasOneTimeSubmission(selectedForm) ? (
                      'Already Submitted'
                    ) : editingSubmissionId ? (
                      'Resubmit Work'
                    ) : (
                      'Submit Work'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Modals for Form View */}
        {showSuccessModal && <SuccessModal />}
        {showSubmissionHistory && <SubmissionHistoryModal />}
      </div>
    );
  }

  // Categorize forms
  const oneTimeForms = programSubmissionForms.filter(f => !f.repeatable);
  const repeatableForms = programSubmissionForms.filter(f => f.repeatable);

  // Get all approved submissions across all forms
  const approvedSubmissions = submissions.filter(s => s.status === 'approved');
  const approvedByForm = {};
  approvedSubmissions.forEach(sub => {
    const formId = sub.submissionFormId?._id || sub.submissionFormId;
    if (!approvedByForm[formId]) approvedByForm[formId] = [];
    approvedByForm[formId].push(sub);
  });

  // Count stats
  const totalForms = programSubmissionForms.length;
  const completedCount = oneTimeForms.filter(f => getLatestSubmissionStatus(f._id) === 'approved').length;
  const pendingCount = programSubmissionForms.filter(f => {
    const status = getLatestSubmissionStatus(f._id);
    return status === 'pending' || status === 'more_info_needed';
  }).length;

  // Tab config
  const tabs = [
    { id: 'repeatable', label: 'Repeatable', icon: Repeat, count: repeatableForms.length },
    { id: 'missions', label: 'Missions', icon: Target, count: oneTimeForms.length },
    { id: 'completed', label: 'Completed', icon: Trophy, count: approvedSubmissions.length },
  ];

  // Card component for a submission form
  const FormCard = ({ form }) => {
    const submissionCount = getSubmissionCount(form._id);
    const latestStatus = getLatestSubmissionStatus(form._id);
    const isApproved = latestStatus === 'approved';
    const isPending = latestStatus === 'pending';
    const needsAction = latestStatus === 'rejected' || latestStatus === 'more_info_needed';
    const isNew = submissionCount === 0;

    const statusConfig = {
      approved: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700', icon: <CheckCircle2 className="w-4 h-4 text-emerald-500" /> },
      pending: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', icon: <Clock className="w-4 h-4 text-amber-500" /> },
      rejected: { bg: 'bg-red-50', border: 'border-red-200', accent: 'text-red-700', icon: <AlertCircle className="w-4 h-4 text-red-500" /> },
      more_info_needed: { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-700', icon: <AlertCircle className="w-4 h-4 text-orange-500" /> },
    };
    const status = statusConfig[latestStatus] || null;

    return (
      <div
        className="bg-card rounded-xl border border-border hover:border-primary hover:shadow-lg transition-all cursor-pointer group overflow-hidden"
        onClick={() => handleFormSelect(form)}
      >
        {/* Top accent bar */}
        <div className={`h-1 ${isApproved ? 'bg-emerald-400' : needsAction ? 'bg-orange-400' : isPending ? 'bg-amber-400' : 'bg-[#1086a4]'}`} />

        <div className="p-5">
          {/* Header row */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2 text-[15px]">
                {form.title}
              </h3>
              {form.experiencePoints > 0 && (
                <div className="flex items-center gap-1 mt-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-semibold text-amber-600">{form.experiencePoints} XP</span>
                </div>
              )}
            </div>
            <ChevronRight className="w-5 h-5 text-foreground/30 group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
          </div>

          {/* Status badge */}
          {status && (
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.accent} mb-3`}>
              {status.icon}
              {getStatusLabel(latestStatus)}
            </div>
          )}
          {isNew && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#1086a4]/10 text-[#1086a4] mb-3">
              <FileText className="w-3.5 h-3.5" />
              Not started
            </div>
          )}

          {/* Requirements pills */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {form.linkRequired && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-[11px] text-foreground/60 font-medium">
                <LinkIcon className="w-3 h-3" /> Link
              </span>
            )}
            {form.commentRequired && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-[11px] text-foreground/60 font-medium">
                <MessageSquare className="w-3 h-3" /> Comment
              </span>
            )}
            {form.achievementImageRequired && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-[11px] text-foreground/60 font-medium">
                <ImageIcon className="w-3 h-3" /> Image
              </span>
            )}
            {form.videoRequired && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-[11px] text-foreground/60 font-medium">
                Video
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-3">
              {submissionCount > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedForm({ ...form, _isHistoryOnly: true });
                    getSubmissions({ userId: user.id });
                    setTimeout(() => setShowSubmissionHistory(true), 100);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#1086a4] font-medium transition-colors"
                >
                  <History className="w-3.5 h-3.5" />
                  {submissionCount} {submissionCount === 1 ? 'submission' : 'submissions'}
                </button>
              )}
            </div>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
              isApproved && !form.repeatable
                ? 'bg-emerald-100 text-emerald-700'
                : needsAction
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-[#1086a4] text-white group-hover:bg-[#054581]'
            }`}>
              {isApproved && !form.repeatable ? 'Completed' : needsAction ? 'Needs Revision' : form.repeatable ? 'Submit' : isPending ? 'Pending' : 'Start'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Completed submission card
  const CompletedCard = ({ formId, formSubmissions: approvedSubs }) => {
    const form = programSubmissionForms.find(f => f._id === formId);
    const formTitle = form?.title || approvedSubs[0]?.submissionFormId?.title || 'Unknown Form';
    const latestSub = approvedSubs[0];

    return (
      <div className="bg-card rounded-xl border border-emerald-200 overflow-hidden">
        <div className="h-1 bg-emerald-400" />
        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground leading-snug text-[15px]">{formTitle}</h3>
              <div className="flex items-center gap-1 mt-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs text-emerald-600 font-medium">Approved</span>
                {latestSub && (
                  <span className="text-xs text-gray-400 ml-2">
                    {new Date(latestSub.reviewedAt || latestSub.submittedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            {form?.experiencePoints > 0 && (
              <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-full">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-xs font-semibold text-amber-600">+{form.experiencePoints * approvedSubs.length} XP</span>
              </div>
            )}
          </div>

          {approvedSubs.length > 1 && (
            <p className="text-xs text-gray-500 mb-3">{approvedSubs.length} approved submissions</p>
          )}

          {latestSub?.link && (
            <a
              href={latestSub.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-xs text-[#1086a4] hover:text-[#054581] font-medium transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              View submitted link
            </a>
          )}

          {form && (
            <div className="flex items-center justify-end pt-3 border-t border-border mt-3">
              <button
                onClick={() => {
                  setSelectedForm({ ...form, _isHistoryOnly: true });
                  getSubmissions({ userId: user.id });
                  setTimeout(() => setShowSubmissionHistory(true), 100);
                }}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#1086a4] font-medium transition-colors"
              >
                <History className="w-3.5 h-3.5" />
                View History
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Show list of submission forms
  return (
    <div className="min-h-screen pt-20 pb-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-[#1086a4] hover:text-[#054581] font-medium mb-6 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-primary">Submissions</h1>
              <p className="text-foreground/50 text-sm mt-1">Complete missions and track your progress</p>
            </div>
            {/* Stats summary */}
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-card rounded-lg border border-border">
                <p className="text-xl font-bold text-foreground">{totalForms}</p>
                <p className="text-[10px] font-medium text-foreground/50 uppercase tracking-wider">Total</p>
              </div>
              <div className="text-center px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-xl font-bold text-emerald-600">{completedCount}</p>
                <p className="text-[10px] font-medium text-emerald-600 uppercase tracking-wider">Done</p>
              </div>
              {pendingCount > 0 && (
                <div className="text-center px-4 py-2 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
                  <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wider">Pending</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Access Denied */}
        {accessDenied && (
          <div className="text-center py-12 bg-card rounded-xl border border-border">
            <ShieldX className="w-14 h-14 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Access Denied</h3>
            <p className="text-foreground/50 mb-6 text-sm">This submission form is not part of your program.</p>
            <button
              onClick={handleBackToList}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg hover:bg-[#054581] transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        )}

        {/* Tabs + Content */}
        {!accessDenied && (
          <>
            {/* Tab bar */}
            <div className="flex border-b border-border mb-6">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-[#1086a4] text-[#1086a4]'
                      : 'border-transparent text-foreground/50 hover:text-foreground hover:border-foreground/30'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                    activeTab === tab.id ? 'bg-[#1086a4]/10 text-[#1086a4]' : 'bg-muted text-foreground/50'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Missions tab */}
            {activeTab === 'missions' && (
              <>
                {oneTimeForms.length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {oneTimeForms.map(form => <FormCard key={form._id} form={form} />)}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Target className="w-12 h-12 text-foreground/30 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-foreground/50 mb-1">No Missions</h3>
                    <p className="text-sm text-foreground/40">No one-time missions assigned to your program yet.</p>
                  </div>
                )}
              </>
            )}

            {/* Repeatable tab */}
            {activeTab === 'repeatable' && (
              <>
                {repeatableForms.length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {repeatableForms.map(form => <FormCard key={form._id} form={form} />)}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Repeat className="w-12 h-12 text-foreground/30 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-foreground/50 mb-1">No Repeatable Forms</h3>
                    <p className="text-sm text-foreground/40">No repeatable submission forms assigned yet.</p>
                  </div>
                )}
              </>
            )}

            {/* Completed tab */}
            {activeTab === 'completed' && (
              <>
                {Object.keys(approvedByForm).length > 0 ? (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(approvedByForm).map(([formId, subs]) => (
                      <CompletedCard key={formId} formId={formId} formSubmissions={subs} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Trophy className="w-12 h-12 text-foreground/30 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-foreground/50 mb-1">No Completed Submissions</h3>
                    <p className="text-sm text-foreground/40">Your approved submissions will appear here.</p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {programSubmissionForms.length === 0 && !accessDenied && (
          <div className="text-center py-16">
            <ClipboardList className="w-14 h-14 text-foreground/30 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground/50 mb-1">No Submission Forms</h3>
            <p className="text-sm text-foreground/40">There are no submission forms assigned to your program.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showSuccessModal && <SuccessModal />}
      {showSubmissionHistory && <SubmissionHistoryModal />}
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  submission: state.submission,
  submissionForm: state.submissionForm
});

export default connect(mapStateToProps, { addSubmission, getSubmissions, getStudentSubmissionForms, getStudentSubmissionForm, updateSubmission })(StudentSubmissionForms);